import { type Accessor, For, Show } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Select from '~/components/Select'
import SwipeAction from '~/components/SwipeAction'
import Switch from '~/components/Switch'
import Table from '~/components/Table'
import { type InventoryMovementType, MOVEMENT_TYPE_OPTIONS, type MovementTypeType, type ProductVariantType, toNumber, variantLabel } from '~/services/product.service'
import { movementColor, movementLabel } from '~/utils/status'

export interface InventarioTabPropsType {
  tracksInventory: Accessor<boolean>
  isEdit: Accessor<boolean>
  isMobile: Accessor<boolean>
  savedVariants: () => ProductVariantType[]
  movementType: Accessor<MovementTypeType>
  setMovementType: (value: MovementTypeType) => void
  movementVariant: Accessor<string>
  setMovementVariant: (value: string) => void
  movementQty: Accessor<string>
  setMovementQty: (value: string) => void
  movementReason: Accessor<string>
  setMovementReason: (value: string) => void
  savingMovement: Accessor<boolean>
  registerMovement: () => void
  movements: () => InventoryMovementType[] | undefined
  movementsLoading: boolean
}

/** Pestaña Inventario: conmutador de inventario, registrar movimientos e historial. */
function InventarioTab(props: InventarioTabPropsType) {
  return (
    <div class="space-y-5">
      <div class="flex flex-wrap items-center gap-6">
        <Switch name="trackInventory" label="Llevar inventario de este producto" checked={props.tracksInventory()} />
        <Show when={props.tracksInventory()}>
          <div class="w-40">
            <Input name="minStock" label="Mínimo" type="number" min="0" helperText="Avisa al bajar de aquí" />
          </div>
        </Show>
      </div>

      <Show
        when={props.isEdit() && props.tracksInventory()}
        fallback={
          <p class="text-sm text-foreground-subtle">
            <Show when={!props.isEdit()} fallback="Activa el inventario y guarda para empezar a registrar movimientos.">
              Guarda el producto y podrás registrar entradas y salidas aquí.
            </Show>
          </p>
        }
      >
        <div class="rounded-lg border border-border p-4">
          <h3 class="font-semibold text-foreground mb-3">Registrar movimiento</h3>
          <div class="flex flex-wrap items-end gap-3">
            <div class="w-44">
              <Select label="Tipo" options={MOVEMENT_TYPE_OPTIONS} value={props.movementType()} onChange={v => props.setMovementType((v || 'in') as MovementTypeType)} />
            </div>
            <Show when={props.savedVariants().length > 0}>
              <div class="w-52">
                <Select
                  label="Variante"
                  options={props.savedVariants().map(v => ({ value: String(v.id), title: variantLabel(v) }))}
                  value={props.movementVariant()}
                  onChange={v => props.setMovementVariant(String(v ?? ''))}
                />
              </div>
            </Show>
            <div class="w-32">
              <Input
                label="Cantidad"
                type="number"
                step="1"
                value={props.movementQty()}
                onInput={e => props.setMovementQty(e.currentTarget.value)}
                helperText={props.movementType() === 'adjust' ? 'Puede ser negativa' : undefined}
              />
            </div>
            <div class="min-w-48 flex-1">
              <Input label="Motivo" placeholder="Compra a proveedor, rotura, conteo…" value={props.movementReason()} onInput={e => props.setMovementReason(e.currentTarget.value)} />
            </div>
            <Button color="info" onClick={props.registerMovement} disabled={props.savingMovement()} prependIcon="material-symbols-light:add-circle-outline">
              {props.savingMovement() ? 'Registrando…' : 'Registrar'}
            </Button>
          </div>
          <p class="text-xs text-foreground-subtle mt-2">Los movimientos no se editan ni se borran: una equivocación se corrige con un ajuste en sentido contrario.</p>
        </div>

        <div>
          <h3 class="font-semibold text-foreground mb-2">Historial</h3>

          <Show when={props.isMobile()}>
            <div class="space-y-2">
              <For each={props.movements() ?? []}>
                {m => (
                  <SwipeAction itemId={`movement-${m.id}`} rememberPosition>
                    <div class="rounded-lg border border-border px-4 py-3 bg-surface">
                      <div class="flex items-center justify-between gap-2">
                        <span class="font-medium text-foreground truncate">{new Date(m.createdAt).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}</span>
                        <Badge color={movementColor[m.type]} variant="soft">
                          {movementLabel[m.type]}
                        </Badge>
                      </div>
                      <div class="flex items-center gap-2 mt-1.5 flex-wrap">
                        <Show when={m.variant != null}>
                          <span class="text-xs text-foreground-subtle">{m.variant ? variantLabel(m.variant) : '—'}</span>
                        </Show>
                        <span
                          class="text-sm font-semibold"
                          classList={{
                            'text-success': m.type === 'in' || (m.type === 'adjust' && toNumber(m.quantity) > 0),
                            'text-danger': m.type === 'out' || (m.type === 'adjust' && toNumber(m.quantity) < 0)
                          }}
                        >
                          {(() => {
                            const qty = toNumber(m.quantity)
                            if (m.type === 'adjust') return `${qty > 0 ? '+' : ''}${qty.toLocaleString('es-CO')}`
                            return `${m.type === 'out' ? '-' : '+'}${Math.abs(qty).toLocaleString('es-CO')}`
                          })()}
                        </span>
                        <span class="text-xs text-foreground-subtle">· saldo {toNumber(m.balance).toLocaleString('es-CO')}</span>
                      </div>
                      <Show when={m.reason || m.user}>
                        <p class="text-xs text-foreground-subtle mt-1 truncate">
                          {m.reason || '—'}
                          <Show when={m.user}>
                            <span> · {m.user!.name}</span>
                          </Show>
                        </p>
                      </Show>
                      <Icon icon="material-symbols-light:chevron-right" size={18} class="absolute top-1/2 -translate-y-1/2 right-3 text-foreground-subtle" />
                    </div>
                  </SwipeAction>
                )}
              </For>

              <Show when={(props.movements() ?? []).length === 0 && !props.movementsLoading}>
                <p class="text-sm text-foreground-subtle">Todavía no hay movimientos</p>
              </Show>
            </div>
          </Show>

          <Show when={!props.isMobile()}>
            <Table<InventoryMovementType>
              data={props.movements() ?? []}
              keyField="id"
              loading={props.movementsLoading}
              emptyMessage="Todavía no hay movimientos"
              columns={[
                { header: 'Fecha', accessor: m => new Date(m.createdAt).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }) },
                {
                  header: 'Tipo',
                  accessor: m => (
                    <Badge color={movementColor[m.type]} variant="soft">
                      {movementLabel[m.type]}
                    </Badge>
                  )
                },
                { header: 'Variante', accessor: m => (m.variant ? variantLabel(m.variant) : '—') },
                {
                  header: 'Cantidad',
                  accessor: m => {
                    const qty = toNumber(m.quantity)
                    if (m.type === 'adjust') return `${qty > 0 ? '+' : ''}${qty.toLocaleString('es-CO')}`
                    return `${m.type === 'out' ? '-' : '+'}${Math.abs(qty).toLocaleString('es-CO')}`
                  }
                },
                { header: 'Saldo', accessor: m => toNumber(m.balance).toLocaleString('es-CO') },
                { header: 'Motivo', accessor: m => m.reason || '—' },
                { header: 'Quién', accessor: m => m.user?.name ?? '—' }
              ]}
            />
          </Show>
        </div>
      </Show>
    </div>
  )
}

export default InventarioTab
